import { PartnerId } from "../../constants";
import {
  AddCommentRequest,
  CreateTeamRequest,
  DefaultTeamMemberData,
  PaginatedTicketsResponse,
  SubmitTicketRequest,
  TeamDetailResponse,
  TicketDetailResponse,
  TicketingAccessInfo,
  TicketOptionsResponse,
  TicketPaginationParams,
  TicketsResponse,
  TicketTeamsAdminResponse,
  UpdateTicketRequest,
} from "../../models/ticketing";
import { TicketingScope } from "../scopes";
import { createOnboardingAsyncAction } from "./helpers";

export enum TicketingActions {
  VERIFY_ACCESS_START = "@ticketing/VERIFY_ACCESS_START",
  VERIFY_ACCESS_COMPLETED = "@ticketing/VERIFY_ACCESS_COMPLETED",
  VERIFY_ACCESS_ERROR = "@ticketing/VERIFY_ACCESS_ERROR",
  FETCH_TICKETS_START = "@ticketing/FETCH_TICKETS_START",
  FETCH_TICKETS_COMPLETED = "@ticketing/FETCH_TICKETS_COMPLETED",
  FETCH_TICKETS_ERROR = "@ticketing/FETCH_TICKETS_ERROR",
  FETCH_OPTIONS_START = "@ticketing/FETCH_OPTIONS_START",
  FETCH_OPTIONS_COMPLETED = "@ticketing/FETCH_OPTIONS_COMPLETED",
  FETCH_OPTIONS_ERROR = "@ticketing/FETCH_OPTIONS_ERROR",
  SUBMIT_TICKET_START = "@ticketing/SUBMIT_TICKET_START",
  SUBMIT_TICKET_COMPLETED = "@ticketing/SUBMIT_TICKET_COMPLETED",
  SUBMIT_TICKET_ERROR = "@ticketing/SUBMIT_TICKET_ERROR",
  FETCH_DETAIL_START = "@ticketing/FETCH_DETAIL_START",
  FETCH_DETAIL_COMPLETED = "@ticketing/FETCH_DETAIL_COMPLETED",
  FETCH_DETAIL_ERROR = "@ticketing/FETCH_DETAIL_ERROR",
  ADD_COMMENT_START = "@ticketing/ADD_COMMENT_START",
  ADD_COMMENT_COMPLETED = "@ticketing/ADD_COMMENT_COMPLETED",
  ADD_COMMENT_ERROR = "@ticketing/ADD_COMMENT_ERROR",
  UPDATE_TICKET_START = "@ticketing/UPDATE_TICKET_START",
  UPDATE_TICKET_COMPLETED = "@ticketing/UPDATE_TICKET_COMPLETED",
  UPDATE_TICKET_ERROR = "@ticketing/UPDATE_TICKET_ERROR",
  UPDATE_NOTIFICATION_EMAIL_START = "@ticketing/UPDATE_NOTIFICATION_EMAIL_START",
  UPDATE_NOTIFICATION_EMAIL_COMPLETED = "@ticketing/UPDATE_NOTIFICATION_EMAIL_COMPLETED",
  UPDATE_NOTIFICATION_EMAIL_ERROR = "@ticketing/UPDATE_NOTIFICATION_EMAIL_ERROR",
  UPDATE_NOTIFICATION_OPT_OUT_START = "@ticketing/UPDATE_NOTIFICATION_OPT_OUT_START",
  UPDATE_NOTIFICATION_OPT_OUT_COMPLETED = "@ticketing/UPDATE_NOTIFICATION_OPT_OUT_COMPLETED",
  UPDATE_NOTIFICATION_OPT_OUT_ERROR = "@ticketing/UPDATE_NOTIFICATION_OPT_OUT_ERROR",
  FETCH_PAGINATED_TICKETS_START = "@ticketing/FETCH_PAGINATED_TICKETS_START",
  FETCH_PAGINATED_TICKETS_COMPLETED = "@ticketing/FETCH_PAGINATED_TICKETS_COMPLETED",
  FETCH_PAGINATED_TICKETS_ERROR = "@ticketing/FETCH_PAGINATED_TICKETS_ERROR",
  SET_PAGINATION_PARAMS = "@ticketing/SET_PAGINATION_PARAMS",
  CLEAR_DETAIL = "@ticketing/CLEAR_DETAIL",
  CLEAR_SUBMIT_STATUS = "@ticketing/CLEAR_SUBMIT_STATUS",
  DISMISS_NEW_CONTACT = "@ticketing/DISMISS_NEW_CONTACT",
  SET_ACCESS_ID = "@ticketing/SET_ACCESS_ID",
  FETCH_TEAMS_ADMIN_START = "@ticketing/FETCH_TEAMS_ADMIN_START",
  FETCH_TEAMS_ADMIN_COMPLETED = "@ticketing/FETCH_TEAMS_ADMIN_COMPLETED",
  FETCH_TEAMS_ADMIN_ERROR = "@ticketing/FETCH_TEAMS_ADMIN_ERROR",
  FETCH_DEFAULT_TEAM_MEMBERS_START = "@ticketing/FETCH_DEFAULT_TEAM_MEMBERS_START",
  FETCH_DEFAULT_TEAM_MEMBERS_COMPLETED = "@ticketing/FETCH_DEFAULT_TEAM_MEMBERS_COMPLETED",
  FETCH_DEFAULT_TEAM_MEMBERS_ERROR = "@ticketing/FETCH_DEFAULT_TEAM_MEMBERS_ERROR",
  CREATE_TEAM_START = "@ticketing/CREATE_TEAM_START",
  CREATE_TEAM_COMPLETED = "@ticketing/CREATE_TEAM_COMPLETED",
  CREATE_TEAM_ERROR = "@ticketing/CREATE_TEAM_ERROR",
  TOGGLE_TEAM_STATUS_START = "@ticketing/TOGGLE_TEAM_STATUS_START",
  TOGGLE_TEAM_STATUS_COMPLETED = "@ticketing/TOGGLE_TEAM_STATUS_COMPLETED",
  TOGGLE_TEAM_STATUS_ERROR = "@ticketing/TOGGLE_TEAM_STATUS_ERROR",
  FETCH_TEAM_DETAIL_START = "@ticketing/FETCH_TEAM_DETAIL_START",
  FETCH_TEAM_DETAIL_COMPLETED = "@ticketing/FETCH_TEAM_DETAIL_COMPLETED",
  FETCH_TEAM_DETAIL_ERROR = "@ticketing/FETCH_TEAM_DETAIL_ERROR",
  REMOVE_TEAM_MEMBER_START = "@ticketing/REMOVE_TEAM_MEMBER_START",
  REMOVE_TEAM_MEMBER_COMPLETED = "@ticketing/REMOVE_TEAM_MEMBER_COMPLETED",
  REMOVE_TEAM_MEMBER_ERROR = "@ticketing/REMOVE_TEAM_MEMBER_ERROR",
  ADD_TEAM_MEMBERS_START = "@ticketing/ADD_TEAM_MEMBERS_START",
  ADD_TEAM_MEMBERS_COMPLETED = "@ticketing/ADD_TEAM_MEMBERS_COMPLETED",
  ADD_TEAM_MEMBERS_ERROR = "@ticketing/ADD_TEAM_MEMBERS_ERROR",
  RENAME_TEAM_START = "@ticketing/RENAME_TEAM_START",
  RENAME_TEAM_COMPLETED = "@ticketing/RENAME_TEAM_COMPLETED",
  RENAME_TEAM_ERROR = "@ticketing/RENAME_TEAM_ERROR",
  ASSIGN_TEAM_MANAGER_START = "@ticketing/ASSIGN_TEAM_MANAGER_START",
  ASSIGN_TEAM_MANAGER_COMPLETED = "@ticketing/ASSIGN_TEAM_MANAGER_COMPLETED",
  ASSIGN_TEAM_MANAGER_ERROR = "@ticketing/ASSIGN_TEAM_MANAGER_ERROR",
  REMOVE_TEAM_MANAGER_START = "@ticketing/REMOVE_TEAM_MANAGER_START",
  REMOVE_TEAM_MANAGER_COMPLETED = "@ticketing/REMOVE_TEAM_MANAGER_COMPLETED",
  REMOVE_TEAM_MANAGER_ERROR = "@ticketing/REMOVE_TEAM_MANAGER_ERROR",
  TOGGLE_MEMBER_STATUS_START = "@ticketing/TOGGLE_MEMBER_STATUS_START",
  TOGGLE_MEMBER_STATUS_COMPLETED = "@ticketing/TOGGLE_MEMBER_STATUS_COMPLETED",
  TOGGLE_MEMBER_STATUS_ERROR = "@ticketing/TOGGLE_MEMBER_STATUS_ERROR",
  CREATE_INVITATION_START = "@ticketing/CREATE_INVITATION_START",
  CREATE_INVITATION_COMPLETED = "@ticketing/CREATE_INVITATION_COMPLETED",
  CREATE_INVITATION_ERROR = "@ticketing/CREATE_INVITATION_ERROR",
  REVOKE_INVITATION_START = "@ticketing/REVOKE_INVITATION_START",
  REVOKE_INVITATION_COMPLETED = "@ticketing/REVOKE_INVITATION_COMPLETED",
  REVOKE_INVITATION_ERROR = "@ticketing/REVOKE_INVITATION_ERROR",
  RENEW_INVITATION_START = "@ticketing/RENEW_INVITATION_START",
  RENEW_INVITATION_COMPLETED = "@ticketing/RENEW_INVITATION_COMPLETED",
  RENEW_INVITATION_ERROR = "@ticketing/RENEW_INVITATION_ERROR",
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ticketingActions: any = {
  verifyAccess: createOnboardingAsyncAction(
    TicketingScope,
    TicketingActions.VERIFY_ACCESS_START,
    () => ({}),
    TicketingActions.VERIFY_ACCESS_COMPLETED,
    (accessInfo: TicketingAccessInfo) => ({ accessInfo }),
    TicketingActions.VERIFY_ACCESS_ERROR,
    (error: Error) => ({ error })
  ),
  fetchTickets: createOnboardingAsyncAction(
    TicketingScope,
    TicketingActions.FETCH_TICKETS_START,
    (accessId?: string) => ({ accessId }),
    TicketingActions.FETCH_TICKETS_COMPLETED,
    (ticketsResponse: TicketsResponse) => ({
      ticketsResponse,
    }),
    TicketingActions.FETCH_TICKETS_ERROR,
    (error: Error) => ({ error })
  ),
  fetchOptions: createOnboardingAsyncAction(
    TicketingScope,
    TicketingActions.FETCH_OPTIONS_START,
    (accessId?: string) => ({ accessId }),
    TicketingActions.FETCH_OPTIONS_COMPLETED,
    (optionsResponse: TicketOptionsResponse) => ({
      optionsResponse,
    }),
    TicketingActions.FETCH_OPTIONS_ERROR,
    (error: Error) => ({ error })
  ),
  submitTicket: createOnboardingAsyncAction(
    TicketingScope,
    TicketingActions.SUBMIT_TICKET_START,
    (request: SubmitTicketRequest) => ({ request }),
    TicketingActions.SUBMIT_TICKET_COMPLETED,
    () => ({}),
    TicketingActions.SUBMIT_TICKET_ERROR,
    (error: Error) => ({ error })
  ),
  fetchDetail: createOnboardingAsyncAction(
    TicketingScope,
    TicketingActions.FETCH_DETAIL_START,
    (ticketId: string) => ({ ticketId }),
    TicketingActions.FETCH_DETAIL_COMPLETED,
    (detailResponse: TicketDetailResponse) => ({
      detailResponse,
    }),
    TicketingActions.FETCH_DETAIL_ERROR,
    (error: Error) => ({ error })
  ),
  addComment: createOnboardingAsyncAction(
    TicketingScope,
    TicketingActions.ADD_COMMENT_START,
    (request: AddCommentRequest) => ({ request }),
    TicketingActions.ADD_COMMENT_COMPLETED,
    (comment: string, authorName?: string) => ({ comment, authorName }),
    TicketingActions.ADD_COMMENT_ERROR,
    (error: Error) => ({ error })
  ),
  updateTicket: createOnboardingAsyncAction(
    TicketingScope,
    TicketingActions.UPDATE_TICKET_START,
    (request: UpdateTicketRequest) => ({ request }),
    TicketingActions.UPDATE_TICKET_COMPLETED,
    (request: UpdateTicketRequest) => ({ request }),
    TicketingActions.UPDATE_TICKET_ERROR,
    (error: Error) => ({ error })
  ),
  updateNotificationEmail: createOnboardingAsyncAction(
    TicketingScope,
    TicketingActions.UPDATE_NOTIFICATION_EMAIL_START,
    (notificationEmail: string) => ({ notificationEmail }),
    TicketingActions.UPDATE_NOTIFICATION_EMAIL_COMPLETED,
    (notificationEmail: string) => ({ notificationEmail }),
    TicketingActions.UPDATE_NOTIFICATION_EMAIL_ERROR,
    (error: Error) => ({ error })
  ),
  updateNotificationOptOut: createOnboardingAsyncAction(
    TicketingScope,
    TicketingActions.UPDATE_NOTIFICATION_OPT_OUT_START,
    (optOut: boolean) => ({ optOut }),
    TicketingActions.UPDATE_NOTIFICATION_OPT_OUT_COMPLETED,
    (optOut: boolean) => ({ optOut }),
    TicketingActions.UPDATE_NOTIFICATION_OPT_OUT_ERROR,
    (error: Error) => ({ error })
  ),
  fetchPaginatedTickets: createOnboardingAsyncAction(
    TicketingScope,
    TicketingActions.FETCH_PAGINATED_TICKETS_START,
    (params: TicketPaginationParams) => ({ params }),
    TicketingActions.FETCH_PAGINATED_TICKETS_COMPLETED,
    (response: PaginatedTicketsResponse) => ({ response }),
    TicketingActions.FETCH_PAGINATED_TICKETS_ERROR,
    (error: Error) => ({ error })
  ),
  setPaginationParams: (params: Partial<TicketPaginationParams>) => ({
    type: TicketingActions.SET_PAGINATION_PARAMS,
    scope: TicketingScope,
    partner: PartnerId,
    params,
  }),
  clearDetail: () => ({
    type: TicketingActions.CLEAR_DETAIL,
    scope: TicketingScope,
    partner: PartnerId,
  }),
  clearSubmitStatus: () => ({
    type: TicketingActions.CLEAR_SUBMIT_STATUS,
    scope: TicketingScope,
    partner: PartnerId,
  }),
  dismissNewContact: () => ({
    type: TicketingActions.DISMISS_NEW_CONTACT,
    scope: TicketingScope,
    partner: PartnerId,
  }),
  setAccessId: (accessId: string) => ({
    type: TicketingActions.SET_ACCESS_ID,
    scope: TicketingScope,
    partner: PartnerId,
    accessId,
  }),
  fetchTeamsAdmin: createOnboardingAsyncAction(
    TicketingScope,
    TicketingActions.FETCH_TEAMS_ADMIN_START,
    (accessId: string) => ({ accessId }),
    TicketingActions.FETCH_TEAMS_ADMIN_COMPLETED,
    (response: TicketTeamsAdminResponse) => ({ response }),
    TicketingActions.FETCH_TEAMS_ADMIN_ERROR,
    (error: Error) => ({ error })
  ),
  fetchDefaultTeamMembers: createOnboardingAsyncAction(
    TicketingScope,
    TicketingActions.FETCH_DEFAULT_TEAM_MEMBERS_START,
    (accessId: string) => ({ accessId }),
    TicketingActions.FETCH_DEFAULT_TEAM_MEMBERS_COMPLETED,
    (members: DefaultTeamMemberData[]) => ({ members }),
    TicketingActions.FETCH_DEFAULT_TEAM_MEMBERS_ERROR,
    (error: Error) => ({ error })
  ),
  createTeam: createOnboardingAsyncAction(
    TicketingScope,
    TicketingActions.CREATE_TEAM_START,
    (request: CreateTeamRequest) => ({ request }),
    TicketingActions.CREATE_TEAM_COMPLETED,
    (teamId: string) => ({ teamId }),
    TicketingActions.CREATE_TEAM_ERROR,
    (error: Error) => ({ error })
  ),
  toggleTeamStatus: createOnboardingAsyncAction(
    TicketingScope,
    TicketingActions.TOGGLE_TEAM_STATUS_START,
    (teamId: string, activate: boolean, accessId: string) => ({ teamId, activate, accessId }),
    TicketingActions.TOGGLE_TEAM_STATUS_COMPLETED,
    () => ({}),
    TicketingActions.TOGGLE_TEAM_STATUS_ERROR,
    (error: Error) => ({ error })
  ),
  fetchTeamDetail: createOnboardingAsyncAction(
    TicketingScope,
    TicketingActions.FETCH_TEAM_DETAIL_START,
    (teamId: string, accessId: string) => ({ teamId, accessId }),
    TicketingActions.FETCH_TEAM_DETAIL_COMPLETED,
    (response: TeamDetailResponse) => ({ response }),
    TicketingActions.FETCH_TEAM_DETAIL_ERROR,
    (error: Error) => ({ error })
  ),
  removeTeamMember: createOnboardingAsyncAction(
    TicketingScope,
    TicketingActions.REMOVE_TEAM_MEMBER_START,
    (teamId: string, contactId: string, accessId: string) => ({ teamId, contactId, accessId }),
    TicketingActions.REMOVE_TEAM_MEMBER_COMPLETED,
    (contactId: string) => ({ contactId }),
    TicketingActions.REMOVE_TEAM_MEMBER_ERROR,
    (error: Error) => ({ error })
  ),
  addTeamMembers: createOnboardingAsyncAction(
    TicketingScope,
    TicketingActions.ADD_TEAM_MEMBERS_START,
    (teamId: string, memberContactIds: string[], accessId: string) => ({
      teamId,
      memberContactIds,
      accessId,
    }),
    TicketingActions.ADD_TEAM_MEMBERS_COMPLETED,
    () => ({}),
    TicketingActions.ADD_TEAM_MEMBERS_ERROR,
    (error: Error) => ({ error })
  ),
  renameTeam: createOnboardingAsyncAction(
    TicketingScope,
    TicketingActions.RENAME_TEAM_START,
    (teamId: string, newName: string, accessId: string) => ({ teamId, newName, accessId }),
    TicketingActions.RENAME_TEAM_COMPLETED,
    (newName: string) => ({ newName }),
    TicketingActions.RENAME_TEAM_ERROR,
    (error: Error) => ({ error })
  ),
  assignTeamManager: createOnboardingAsyncAction(
    TicketingScope,
    TicketingActions.ASSIGN_TEAM_MANAGER_START,
    (teamId: string, contactIds: string[], accessId: string) => ({
      teamId,
      contactIds,
      accessId,
    }),
    TicketingActions.ASSIGN_TEAM_MANAGER_COMPLETED,
    () => ({}),
    TicketingActions.ASSIGN_TEAM_MANAGER_ERROR,
    (error: Error) => ({ error })
  ),
  removeTeamManager: createOnboardingAsyncAction(
    TicketingScope,
    TicketingActions.REMOVE_TEAM_MANAGER_START,
    (teamId: string, contactId: string, accessId: string) => ({ teamId, contactId, accessId }),
    TicketingActions.REMOVE_TEAM_MANAGER_COMPLETED,
    () => ({}),
    TicketingActions.REMOVE_TEAM_MANAGER_ERROR,
    (error: Error) => ({ error })
  ),
  toggleMemberStatus: createOnboardingAsyncAction(
    TicketingScope,
    TicketingActions.TOGGLE_MEMBER_STATUS_START,
    (teamId: string, contactId: string, activate: boolean, accessId: string) => ({
      teamId,
      contactId,
      activate,
      accessId,
    }),
    TicketingActions.TOGGLE_MEMBER_STATUS_COMPLETED,
    (contactId: string, activate: boolean) => ({ contactId, activate }),
    TicketingActions.TOGGLE_MEMBER_STATUS_ERROR,
    (error: Error) => ({ error })
  ),
  createInvitation: createOnboardingAsyncAction(
    TicketingScope,
    TicketingActions.CREATE_INVITATION_START,
    (
      teamId: string,
      loginEmail: string,
      notificationEmail: string | undefined,
      accessId: string
    ) => ({
      teamId,
      loginEmail,
      notificationEmail,
      accessId,
    }),
    TicketingActions.CREATE_INVITATION_COMPLETED,
    () => ({}),
    TicketingActions.CREATE_INVITATION_ERROR,
    (error: Error) => ({ error })
  ),
  revokeInvitation: createOnboardingAsyncAction(
    TicketingScope,
    TicketingActions.REVOKE_INVITATION_START,
    (teamId: string, invitationId: string, accessId: string) => ({
      teamId,
      invitationId,
      accessId,
    }),
    TicketingActions.REVOKE_INVITATION_COMPLETED,
    () => ({}),
    TicketingActions.REVOKE_INVITATION_ERROR,
    (error: Error) => ({ error })
  ),
  renewInvitation: createOnboardingAsyncAction(
    TicketingScope,
    TicketingActions.RENEW_INVITATION_START,
    (teamId: string, invitationId: string, accessId: string) => ({
      teamId,
      invitationId,
      accessId,
    }),
    TicketingActions.RENEW_INVITATION_COMPLETED,
    () => ({}),
    TicketingActions.RENEW_INVITATION_ERROR,
    (error: Error) => ({ error })
  ),
};
